// Editor de contenido de un documento: Word (HTML editable con
// contentEditable), Excel (tabla editable celda por celda) o solo visor
// para PDF/imagen. Al guardar, el backend crea una versión nueva
// automáticamente (ver routes/edit.js), por eso se pide siempre una
// descripción del cambio antes de confirmar.
import { useState, useEffect, useRef } from "react";
import { motion, AnimatePresence } from "framer-motion";
import DOMPurify from "dompurify";
import { api } from "../api";
import { formatDateTime } from "../utils/dates";
import {
  X, Save, Loader, AlertCircle, FileText, Table,
  History, Download, Clock, ChevronRight,
} from "lucide-react";

// Origen del backend para armar URLs de /uploads (content.url ya viene
// como ruta relativa desde el servidor, ver routes/edit.js).
const API_ORIGIN = process.env.REACT_APP_API_URL || "http://localhost:4000";

export default function FileEditor({ doc, onClose }) {
  const [content, setContent]         = useState(null);
  const [loading, setLoading]         = useState(true);
  const [saving, setSaving]           = useState(false);
  const [error, setError]             = useState(null);
  const [saved, setSaved]             = useState(false);
  const [activeSheet, setActiveSheet] = useState(0);

  // Control de versiones
  const [showSaveDialog, setShowSaveDialog]     = useState(false);
  const [changeDesc, setChangeDesc]             = useState("");
  const [descError, setDescError]               = useState(false);
  const [showHistory, setShowHistory]           = useState(false);
  const [versions, setVersions]                 = useState([]);
  const [loadingVersions, setLoadingVersions]   = useState(false);
  const [currentVersion, setCurrentVersion]     = useState(doc.version);

  const editorRef = useRef();
  const descRef   = useRef();

  useEffect(() => {
    api.getContent(doc.id)
      .then(data => { setContent(data); setLoading(false); })
      .catch(e   => { setError("No se pudo cargar el archivo: " + e.message); setLoading(false); });
  }, [doc.id]);

  useEffect(() => {
    if (!showHistory) return;
    setLoadingVersions(true);
    api.getVersions(doc.id)
      .then(v  => { setVersions(v.sort((a, b) => new Date(b.created_at) - new Date(a.created_at))); setLoadingVersions(false); })
      .catch(() => setLoadingVersions(false));
  }, [showHistory, doc.id]);

  // Abre el diálogo de descripción en lugar de guardar directamente
  const handleSave = () => {
    setChangeDesc("");
    setDescError(false);
    setShowSaveDialog(true);
    setTimeout(() => descRef.current?.focus(), 50);
  };

  // Envía el contenido editado al backend (que se encarga de versionar el
  // archivo anterior) y refresca el panel de historial si está abierto.
  const handleConfirmSave = async () => {
    if (!changeDesc.trim()) { setDescError(true); return; }
    setShowSaveDialog(false);
    setSaving(true);
    try {
      let result;
      if (content.type === "word") {
        result = await api.saveContent(doc.id, {
          type: "word",
          content: { html: editorRef.current.innerHTML },
          changeDescription: changeDesc.trim(),
        });
      } else if (content.type === "excel") {
        result = await api.saveContent(doc.id, {
          type: "excel",
          content: { sheets: content.sheets },
          changeDescription: changeDesc.trim(),
        });
      }
      if (result?.version) setCurrentVersion(result.version);
      setSaved(true);
      setTimeout(() => setSaved(false), 2500);
      if (showHistory) {
        api.getVersions(doc.id).then(v =>
          setVersions(v.sort((a, b) => new Date(b.created_at) - new Date(a.created_at)))
        );
      }
    } catch (e) {
      setError("Error guardando: " + e.message);
    } finally {
      setSaving(false);
      setChangeDesc("");
    }
  };

  // Edita una celda del Excel en memoria (el guardado real ocurre al presionar "Guardar")
  const updateCell = (sheetName, rowIdx, colIdx, value) => {
    setContent(prev => {
      const sheets = { ...prev.sheets };
      const rows   = sheets[sheetName].map(r => [...r]);
      if (!rows[rowIdx]) rows[rowIdx] = [];
      rows[rowIdx][colIdx] = value;
      sheets[sheetName] = rows;
      return { ...prev, sheets };
    });
  };

  const canEdit = content?.type === "word" || content?.type === "excel";

  return (
    <>
      <motion.div className="modal-overlay"
        initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
        transition={{ duration: 0.2 }}
        onClick={e => e.target === e.currentTarget && onClose()}>
        <motion.div
          className="modal modal-large"
          style={{ maxWidth: showHistory ? 1200 : 900, height: "88vh", transition: "max-width 0.25s" }}
          initial={{ opacity: 0, scale: 0.92, y: 20 }}
          animate={{ opacity: 1, scale: 1, y: 0 }}
          exit={{ opacity: 0, scale: 0.94, y: 12 }}
          transition={{ type: "spring", stiffness: 380, damping: 30, mass: 0.7 }}>

          {/* Header */}
          <div className="modal-header">
            <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
              {content?.type === "excel"
                ? <Table size={18} style={{ color: "var(--green)" }} />
                : <FileText size={18} style={{ color: "var(--primary)" }} />}
              <div>
                <h2>{doc.name}</h2>
                <p style={{ fontSize: "0.72rem", color: "var(--text-muted)", display: "flex", alignItems: "center", gap: 6 }}>
                  {content?.type === "word"  && "Editor de Word"}
                  {content?.type === "excel" && "Editor de Excel"}
                  {content?.type === "pdf"   && "Visor de PDF"}
                  <span style={{
                    background: "#E6FAF8", color: "var(--primary)",
                    fontWeight: 700, padding: "1px 7px", borderRadius: 4,
                    fontSize: "0.68rem", fontFamily: "IBM Plex Mono, monospace",
                  }}>
                    {currentVersion}
                  </span>
                </p>
              </div>
            </div>
            <div style={{ display: "flex", gap: 8 }}>
              <button
                className="btn-secondary"
                onClick={() => setShowHistory(h => !h)}
                style={{ fontSize: "0.8rem", padding: "6px 12px" }}
              >
                <History size={14} />
                Historial
                {showHistory && <ChevronRight size={13} style={{ transform: "rotate(180deg)" }} />}
              </button>
              {canEdit && (
                <button className="btn-primary" onClick={handleSave} disabled={saving}>
                  {saving ? <Loader size={15} className="spin" /> : <Save size={15} />}
                  {saved ? "¡Guardado!" : "Guardar"}
                </button>
              )}
              <button className="btn-icon" onClick={onClose}><X size={20} /></button>
            </div>
          </div>

          {/* Body: editor + panel historial */}
          <div style={{ flex: 1, overflow: "hidden", display: "flex" }}>

            {/* Contenido principal */}
            <div style={{ flex: 1, overflow: "hidden", display: "flex", flexDirection: "column" }}>
              {loading && (
                <div style={{ flex: 1, display: "flex", alignItems: "center", justifyContent: "center", gap: 12, color: "var(--text-muted)" }}>
                  <Loader size={24} className="spin" /> Cargando archivo...
                </div>
              )}

              {error && (
                <div style={{ margin: 24, padding: "14px 18px", background: "#FEE2E2", borderRadius: 8, border: "1px solid #FCA5A5", display: "flex", gap: 10, alignItems: "center", color: "#C53030", fontSize: "0.85rem" }}>
                  <AlertCircle size={18} /> {error}
                </div>
              )}

              {/* WORD EDITOR */}
              {!loading && content?.type === "word" && (
                <div style={{ flex: 1, overflow: "auto", padding: "0 24px 24px" }}>
                  <div
                    ref={editorRef}
                    contentEditable
                    suppressContentEditableWarning
                    dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(content.html || "<p>Documento vacío</p>") }}
                    style={{
                      minHeight: 400, outline: "none",
                      fontFamily: "Georgia, serif", fontSize: "0.95rem",
                      lineHeight: 1.8, color: "var(--text)",
                      padding: "32px 48px", background: "#fff",
                      border: "1px solid var(--border)", borderRadius: 8,
                      marginTop: 16, boxShadow: "0 2px 12px rgba(0,0,0,0.06)",
                    }}
                  />
                  <p style={{ fontSize: "0.7rem", color: "var(--text-muted)", marginTop: 8, textAlign: "center" }}>
                    Edita el contenido directamente. Los cambios se guardan en el servidor.
                  </p>
                </div>
              )}

              {/* EXCEL EDITOR */}
              {!loading && content?.type === "excel" && (
                <div style={{ flex: 1, display: "flex", flexDirection: "column", overflow: "hidden" }}>
                  {content.sheetNames.length > 1 && (
                    <div style={{ display: "flex", gap: 0, padding: "0 24px", borderBottom: "1px solid var(--border)" }}>
                      {content.sheetNames.map((name, i) => (
                        <button key={name} onClick={() => setActiveSheet(i)}
                          style={{
                            padding: "8px 16px", border: "none", background: "transparent",
                            fontFamily: "inherit", fontSize: "0.78rem", cursor: "pointer",
                            color: activeSheet === i ? "var(--primary)" : "var(--text-muted)",
                            borderBottom: activeSheet === i ? "2px solid var(--primary)" : "2px solid transparent",
                            fontWeight: activeSheet === i ? 600 : 400,
                          }}>
                          {name}
                        </button>
                      ))}
                    </div>
                  )}
                  <div style={{ flex: 1, overflow: "auto", padding: 16 }}>
                    <table style={{ borderCollapse: "collapse", fontSize: "0.78rem", width: "100%" }}>
                      <thead>
                        <tr>
                          <th style={thStyle("#f8fafc")}>#</th>
                          {(content.sheets[content.sheetNames[activeSheet]]?.[0] || []).map((_, ci) => (
                            <th key={ci} style={thStyle("#f8fafc")}>{colLetter(ci)}</th>
                          ))}
                        </tr>
                      </thead>
                      <tbody>
                        {(content.sheets[content.sheetNames[activeSheet]] || []).map((row, ri) => (
                          <tr key={ri}>
                            <td style={{ ...tdStyle, background: "#f8fafc", fontWeight: 600, textAlign: "center", color: "var(--text-muted)", minWidth: 36 }}>{ri + 1}</td>
                            {row.map((cell, ci) => (
                              <td key={ci} style={{ padding: 0, border: "1px solid var(--border)" }}>
                                <input
                                  value={cell ?? ""}
                                  onChange={e => updateCell(content.sheetNames[activeSheet], ri, ci, e.target.value)}
                                  style={{
                                    width: "100%", minWidth: 90, padding: "5px 8px",
                                    border: "none", outline: "none",
                                    background: ri === 0 ? "#F0FDFA" : "transparent",
                                    fontWeight: ri === 0 ? 600 : 400,
                                    fontFamily: "inherit", fontSize: "0.78rem", color: "var(--text)",
                                  }}
                                />
                              </td>
                            ))}
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}

              {/* PDF VIEWER */}
              {!loading && content?.type === "pdf" && (
                <iframe
                  src={`${API_ORIGIN}${content.url}`}
                  style={{ flex: 1, border: "none", width: "100%" }}
                  title={doc.name}
                />
              )}

              {/* IMAGEN */}
              {!loading && content?.type === "image" && (
                <div style={{ flex: 1, display: "flex", alignItems: "center", justifyContent: "center", padding: 24 }}>
                  <img src={`${API_ORIGIN}${content.url}`} alt={doc.name} style={{ maxWidth: "100%", maxHeight: "100%", borderRadius: 8 }} />
                </div>
              )}
            </div>

            {/* Panel de historial */}
            <AnimatePresence>
              {showHistory && (
                <motion.div
                  initial={{ width: 0, opacity: 0 }}
                  animate={{ width: 300, opacity: 1 }}
                  exit={{ width: 0, opacity: 0 }}
                  transition={{ type: "spring", stiffness: 340, damping: 30 }}
                  style={{
                    borderLeft: "1px solid var(--border)",
                    display: "flex", flexDirection: "column", flexShrink: 0,
                    background: "var(--bg)", overflow: "hidden",
                  }}
                >
                  <div style={{ padding: "14px 16px", borderBottom: "1px solid var(--border)", background: "var(--surface)", flexShrink: 0 }}>
                    <p style={{ fontWeight: 700, fontSize: "0.85rem", display: "flex", alignItems: "center", gap: 7, whiteSpace: "nowrap" }}>
                      <History size={15} style={{ color: "var(--primary)" }} /> Historial de versiones
                    </p>
                  </div>

                  <div style={{ flex: 1, overflow: "auto", padding: "8px 0" }}>
                    {loadingVersions && (
                      <div style={{ display: "flex", alignItems: "center", gap: 8, padding: 16, color: "var(--text-muted)", fontSize: "0.8rem" }}>
                        <Loader size={14} className="spin" /> Cargando...
                      </div>
                    )}

                    {!loadingVersions && versions.length === 0 && (
                      <div style={{ padding: 20, textAlign: "center", color: "var(--text-muted)", fontSize: "0.8rem" }}>
                        <Clock size={28} style={{ marginBottom: 8, opacity: 0.35 }} />
                        <p>Sin versiones anteriores</p>
                        <p style={{ fontSize: "0.72rem", marginTop: 4 }}>Guarda el documento para crear la primera versión</p>
                      </div>
                    )}

                    {!loadingVersions && versions.map(v => (
                      <div key={v.id} style={{
                        margin: "0 8px 8px", padding: "12px 14px",
                        background: "var(--surface)", borderRadius: 8,
                        border: "1px solid var(--border)",
                      }}>
                        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 6 }}>
                          <span style={{
                            fontFamily: "IBM Plex Mono, monospace", fontSize: "0.7rem",
                            fontWeight: 700, color: "var(--primary)",
                            background: "#E6FAF8", padding: "2px 8px", borderRadius: 4,
                            whiteSpace: "nowrap",
                          }}>
                            {v.version}
                          </span>
                          <a
                            href={api.versionFileUrl(v.filename)}
                            download={v.originalname}
                            title="Descargar esta versión"
                            style={{
                              display: "inline-flex", alignItems: "center", gap: 4,
                              fontSize: "0.72rem", color: "var(--text-muted)",
                              textDecoration: "none", padding: "3px 7px",
                              border: "1px solid var(--border)", borderRadius: 5,
                              whiteSpace: "nowrap",
                            }}
                          >
                            <Download size={11} /> Descargar
                          </a>
                        </div>

                        {v.change_description && (
                          <p style={{ fontSize: "0.78rem", color: "var(--text)", marginBottom: 5, lineHeight: 1.45 }}>
                            {v.change_description}
                          </p>
                        )}

                        <p style={{ fontSize: "0.68rem", color: "var(--text-muted)", display: "flex", alignItems: "center", gap: 4 }}>
                          <Clock size={10} /> {formatDateTime(v.created_at)}
                        </p>
                      </div>
                    ))}
                  </div>
                </motion.div>
              )}
            </AnimatePresence>
          </div>
        </motion.div>
      </motion.div>

      {/* Modal: descripción de cambios (encima de todo) */}
      <AnimatePresence>
        {showSaveDialog && (
          <motion.div
            initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            style={{
              position: "fixed", inset: 0,
              background: "rgba(15,23,42,0.65)",
              display: "flex", alignItems: "center", justifyContent: "center",
              zIndex: 200, padding: 20,
            }}
            onClick={e => e.target === e.currentTarget && setShowSaveDialog(false)}
          >
            <motion.div
              initial={{ opacity: 0, scale: 0.9, y: 16 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.94 }}
              transition={{ type: "spring", stiffness: 400, damping: 28 }}
              style={{
                background: "var(--surface)", borderRadius: 14, width: "100%", maxWidth: 440,
                boxShadow: "0 20px 60px rgba(0,0,0,0.3)", overflow: "hidden",
              }}
            >
              <div style={{ padding: "20px 24px 16px", borderBottom: "1px solid var(--border)", display: "flex", alignItems: "center", gap: 10 }}>
                <Save size={17} style={{ color: "var(--primary)" }} />
                <div>
                  <h3 style={{ fontSize: "0.95rem", fontWeight: 700 }}>Guardar nueva versión</h3>
                  <p style={{ fontSize: "0.72rem", color: "var(--text-muted)", marginTop: 2 }}>
                    {currentVersion} → {bumpVersionDisplay(currentVersion)}
                  </p>
                </div>
              </div>

              <div style={{ padding: "18px 24px" }}>
                <label style={{ fontSize: "0.78rem", fontWeight: 500, color: "var(--text-muted)", display: "block", marginBottom: 6 }}>
                  ¿Qué cambios realizaste? <span style={{ color: "var(--danger)" }}>*</span>
                </label>
                <textarea
                  ref={descRef}
                  value={changeDesc}
                  onChange={e => { setChangeDesc(e.target.value); if (e.target.value.trim()) setDescError(false); }}
                  onKeyDown={e => { if (e.key === "Enter" && (e.ctrlKey || e.metaKey)) handleConfirmSave(); }}
                  placeholder="Ej: Se actualizaron los valores de la tabla de precios..."
                  rows={4}
                  style={{
                    width: "100%", padding: "9px 12px",
                    border: `1px solid ${descError ? "var(--danger)" : "var(--border)"}`,
                    borderRadius: 8, fontFamily: "inherit", fontSize: "0.85rem",
                    color: "var(--text)", outline: "none", resize: "vertical",
                    background: "var(--bg)", transition: "border-color 0.15s",
                  }}
                />
                {descError && (
                  <p style={{ fontSize: "0.73rem", color: "var(--danger)", marginTop: 5, display: "flex", alignItems: "center", gap: 5 }}>
                    <AlertCircle size={12} /> La descripción es obligatoria.
                  </p>
                )}
                <p style={{ fontSize: "0.68rem", color: "var(--text-muted)", marginTop: 6 }}>
                  Ctrl + Enter para guardar rápido
                </p>
              </div>

              <div style={{ padding: "12px 24px 20px", display: "flex", justifyContent: "flex-end", gap: 10 }}>
                <button className="btn-secondary" onClick={() => setShowSaveDialog(false)}>
                  Cancelar
                </button>
                <button className="btn-primary" onClick={handleConfirmSave}>
                  <Save size={14} /> Guardar versión
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}

function bumpVersionDisplay(v) {
  const m = v?.match(/^v?(\d+)\.(\d+)$/);
  return m ? `v${m[1]}.${parseInt(m[2]) + 1}` : (v ? `${v}.1` : "v1.1");
}

const thStyle = (bg) => ({
  padding: "6px 12px", background: bg, border: "1px solid var(--border)",
  fontWeight: 600, color: "var(--text-muted)", textAlign: "center", position: "sticky", top: 0,
});
const tdStyle = { padding: "4px 8px", border: "1px solid var(--border)" };
const colLetter = (n) => String.fromCharCode(65 + n);
